/* eslint-disable react/no-unstable-nested-components */
import classNames from 'classnames';
import { InjectComponentProvider } from '$util/InjectComponent';
import type { AutocompleteInjectParams } from '$components/basic/Autocomplete';
import { Autocomplete, AutocompleteInjectName } from '$components/basic/Autocomplete';
import type { SuggestionMatchBase } from './components/SuggestionMatch';
import { SuggestionMatch } from './components/SuggestionMatch';
import type { SearchSuggestionsDesktopProps } from './desktop';
import { SearchSuggestionsDesktop } from './desktop';
import type { SearchSuggestionsMobileProps } from './mobile';
import { SearchSuggestionsMobile } from './mobile';

export type { SuggestionMatchBase };

export type SearchSuggestionsProps<TSuggestion, TSection = unknown> = SearchSuggestionsDesktopProps<
  TSuggestion,
  TSection
> &
  SearchSuggestionsMobileProps<TSuggestion, TSection>;

/**
 * This component enhances `Autocomplete` component with specific behaviours for desktop and mobile.
 * Features:
 * * highlight typed value in suggestions list
 * * on mobile open in a modal
 */
export function SearchSuggestions<TSuggestion extends SuggestionMatchBase, TSection = unknown>({
  getSuggestionValue,
  onSuggestionHighlighted,
  onSuggestionSelected,
  onFocus,
  onBlur,
  onClear,
  onKeyUp,
  className,
  ...props
}: SearchSuggestionsProps<TSuggestion, TSection>): JSX.Element {
  return (
    <InjectComponentProvider
      value={{
        [Autocomplete.displayName]: {
          [AutocompleteInjectName.SUGGESTION]: ({
            value,
            suggestion,
          }: AutocompleteInjectParams<TSuggestion>[AutocompleteInjectName.SUGGESTION]) => (
            <SuggestionMatch<TSuggestion>
              value={value}
              suggestion={suggestion}
              getSuggestionValue={getSuggestionValue}
            />
          ),
        },
      }}
    >
      <SearchSuggestionsDesktop<TSuggestion, TSection>
        className={classNames('SearchSuggestions', 'from-md', className)}
        getSuggestionValue={getSuggestionValue}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        onBlur={onBlur}
        onClear={onClear}
        onSuggestionHighlighted={onSuggestionHighlighted}
        onSuggestionSelected={onSuggestionSelected}
        {...props}
      />
      <SearchSuggestionsMobile<TSuggestion, TSection>
        className={classNames('SearchSuggestions', 'to-md', className)}
        getSuggestionValue={getSuggestionValue}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        onBlur={onBlur}
        onClear={onClear}
        onSuggestionHighlighted={onSuggestionHighlighted}
        onSuggestionSelected={onSuggestionSelected}
        {...props}
      />
    </InjectComponentProvider>
  );
}
SearchSuggestions.displayName = 'SearchSuggestions';
